import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Bell, BellRing, Loader2, Mail, MonitorSmartphone, Smartphone } from "lucide-react";
import { toast } from "sonner";
import {
  listSubscriptions,
  pushSupported,
  subscribeToPush,
  unsubscribeFromPush,
} from "@/lib/push";

const FREQ_OPTIONS = [
  { value: "immediate", label: "Immediate — as soon as a nudge is generated" },
  { value: "daily_digest", label: "Daily digest — one email at 9am local" },
  { value: "weekly_digest", label: "Weekly digest — Monday 9am local" },
];

const DEFAULT_PREFS = {
  pace_nudges: {
    email: false,
    web_push: false,
    frequency: "immediate",
    quiet_hours: { enabled: false, start_local: "22:00", end_local: "07:00" },
  },
};

export default function NotificationPreferencesPage() {
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subs, setSubs] = useState([]);
  const [pushBusy, setPushBusy] = useState(false);
  // Phase 31 — timesheet reminder channel preferences (separate collection)
  const [reminderPrefs, setReminderPrefs] = useState(null);
  const [reminderSaving, setReminderSaving] = useState(false);
  const supported = pushSupported();
  const denied = typeof Notification !== "undefined" && Notification.permission === "denied";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: p }, { data: r }] = await Promise.all([
        api.get("/pace/preferences"),
        api.get("/me/reminder-preferences"),
      ]);
      setPrefs({
        ...DEFAULT_PREFS,
        ...p,
        pace_nudges: {
          ...DEFAULT_PREFS.pace_nudges,
          ...(p?.pace_nudges || {}),
          quiet_hours: {
            ...DEFAULT_PREFS.pace_nudges.quiet_hours,
            ...(p?.pace_nudges?.quiet_hours || {}),
          },
        },
      });
      setReminderPrefs(r);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
    if (supported) {
      try {
        const list = await listSubscriptions();
        setSubs(list.items || []);
      } catch (_) {
        setSubs([]);
      }
    }
  }, [supported]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (nextPace) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/pace/preferences", { pace_nudges: nextPace });
      setPrefs({ ...prefs, ...data });
      toast.success("Preferences saved");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const setPace = (patchObj) => {
    const next = { ...prefs.pace_nudges, ...patchObj };
    setPrefs({ ...prefs, pace_nudges: next });
    patch(next);
  };

  const setQuiet = (patchObj) => {
    const nextQh = { ...prefs.pace_nudges.quiet_hours, ...patchObj };
    const next = { ...prefs.pace_nudges, quiet_hours: nextQh };
    setPrefs({ ...prefs, pace_nudges: next });
    patch(next);
  };

  const patchReminder = async (channel, enabled) => {
    const field = `${channel}_enabled`;
    const prev = reminderPrefs;
    setReminderPrefs({ ...prev, [field]: enabled });
    setReminderSaving(true);
    try {
      const { data } = await api.patch("/me/reminder-preferences", { [field]: enabled });
      setReminderPrefs(data);
      toast.success(`${channel === "email" ? "Email" : "In-app"} reminders ${enabled ? "on" : "off"}`);
    } catch (e) {
      setReminderPrefs(prev);
      toast.error(formatApiError(e));
    } finally {
      setReminderSaving(false);
    }
  };

  const onTogglePush = async (checked) => {
    setPushBusy(true);
    try {
      if (checked) {
        await subscribeToPush();
        toast.success("Push notifications enabled on this device");
      } else {
        await unsubscribeFromPush();
        toast.success("Push notifications disabled");
      }
      setPace({ web_push: checked });
      const list = await listSubscriptions();
      setSubs(list.items || []);
    } catch (e) {
      toast.error(e?.message || formatApiError(e));
    } finally {
      setPushBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground" data-testid="prefs-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading preferences…
      </div>
    );
  }

  const pace = prefs.pace_nudges;
  const quiet = pace.quiet_hours;
  const activeSubs = subs.filter((s) => !s.disabled_at);

  return (
    <div className="space-y-8" data-testid="notification-preferences-page">
      <div>
        <div className="text-xs font-medium uppercase tracking-widest text-muted-foreground">Settings</div>
        <h1 className="mt-1 font-display text-3xl font-semibold text-primary">Notifications</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Pace watches your time-tracking so you don't have to. Choose how you want to hear from it.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellRing className="h-4 w-4 text-secondary" />
            Pace nudges
          </CardTitle>
          <CardDescription>
            Missing tags, unsubmitted entries, weekly recap — the same nudges you see in the app,
            delivered where you'll actually see them.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="flex items-center justify-between rounded-xl border border-border p-4">
            <div className="flex items-start gap-3">
              <Mail className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-sm font-medium text-primary">Email</div>
                <div className="text-xs text-muted-foreground">Sends from your workspace's verified sender.</div>
              </div>
            </div>
            <Switch
              checked={pace.email}
              onCheckedChange={(v) => setPace({ email: v })}
              disabled={saving}
              data-testid="prefs-toggle-email"
            />
          </div>

          <div className="flex items-center justify-between rounded-xl border border-border p-4">
            <div className="flex items-start gap-3">
              <MonitorSmartphone className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-sm font-medium text-primary">Browser push</div>
                <div className="text-xs text-muted-foreground">
                  {!supported && "Your browser doesn't support push notifications."}
                  {supported && !denied && (activeSubs.length
                    ? `Active on ${activeSubs.length} device${activeSubs.length === 1 ? "" : "s"}.`
                    : "Enable to receive push notifications on this browser.")}
                  {supported && denied && "Blocked in browser settings — re-enable in site permissions."}
                </div>
              </div>
            </div>
            <Switch
              checked={pace.web_push && activeSubs.length > 0}
              onCheckedChange={onTogglePush}
              disabled={!supported || pushBusy || saving || denied}
              data-testid="prefs-toggle-push"
            />
          </div>

          {supported && denied && (
            <Alert variant="destructive" data-testid="prefs-push-denied">
              <AlertDescription>
                Notifications are blocked at the browser level. Open your browser's site permissions,
                allow notifications for this site, then reload.
              </AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label htmlFor="freq" className="text-sm font-medium text-primary">Cadence</Label>
            <Select
              value={pace.frequency}
              onValueChange={(v) => setPace({ frequency: v })}
              disabled={saving}
            >
              <SelectTrigger id="freq" data-testid="prefs-freq" className="w-full max-w-md">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {FREQ_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value} data-testid={`prefs-freq-${o.value}`}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="rounded-xl border border-border p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-medium text-primary">Quiet hours</div>
                <div className="text-xs text-muted-foreground">
                  Skip email + push during a window each day (times in your workspace timezone).
                </div>
              </div>
              <Switch
                checked={!!quiet.enabled}
                onCheckedChange={(v) => setQuiet({ enabled: v })}
                disabled={saving}
                data-testid="prefs-toggle-quiet"
              />
            </div>
            {quiet.enabled && (
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="qh-start" className="text-xs">Start</Label>
                  <Input
                    id="qh-start"
                    type="time"
                    value={quiet.start_local}
                    onChange={(e) => setQuiet({ start_local: e.target.value })}
                    disabled={saving}
                    data-testid="prefs-quiet-start"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="qh-end" className="text-xs">End</Label>
                  <Input
                    id="qh-end"
                    type="time"
                    value={quiet.end_local}
                    onChange={(e) => setQuiet({ end_local: e.target.value })}
                    disabled={saving}
                    data-testid="prefs-quiet-end"
                  />
                </div>
              </div>
            )}
          </div>

          {supported && activeSubs.length > 0 && (
            <div className="rounded-xl bg-accent/40 p-3 text-xs text-muted-foreground">
              <div className="flex items-center gap-2 font-medium text-primary">
                <Bell className="h-3.5 w-3.5" /> Push endpoints on file
              </div>
              <ul className="mt-2 space-y-1">
                {activeSubs.slice(0, 5).map((s) => (
                  <li key={s.id} className="truncate" data-testid={`prefs-sub-${s.id}`}>
                    <span className="font-mono">{s.endpoint_masked}</span>
                    {s.user_agent && <span className="opacity-60"> · {s.user_agent.slice(0, 50)}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="timesheet-reminders-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BellRing className="h-4 w-4 text-secondary" />
            Timesheet reminders
          </CardTitle>
          <CardDescription>
            Friday 5 PM nudge if your weekly timesheet is still open. Admins may also trigger
            a reminder manually; the same opt-outs apply.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between rounded-xl border border-border p-4">
            <div className="flex items-start gap-3">
              <Mail className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-sm font-medium text-primary">Email</div>
                <div className="text-xs text-muted-foreground">
                  Reminder email to the address on your profile.
                </div>
              </div>
            </div>
            <Switch
              checked={!!reminderPrefs?.email_enabled}
              onCheckedChange={(v) => patchReminder("email", v)}
              disabled={!reminderPrefs || reminderSaving}
              data-testid="reminder-pref-email"
            />
          </div>

          <div className="flex items-center justify-between rounded-xl border border-border p-4">
            <div className="flex items-start gap-3">
              <Bell className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-sm font-medium text-primary">In-app</div>
                <div className="text-xs text-muted-foreground">
                  Shows in the bell menu next to your avatar.
                </div>
              </div>
            </div>
            <Switch
              checked={!!reminderPrefs?.in_app_enabled}
              onCheckedChange={(v) => patchReminder("in_app", v)}
              disabled={!reminderPrefs || reminderSaving}
              data-testid="reminder-pref-in-app"
            />
          </div>

          <div className="flex items-center justify-between rounded-xl border border-dashed border-border p-4 opacity-70">
            <div className="flex items-start gap-3">
              <Smartphone className="mt-0.5 h-4 w-4 text-muted-foreground" />
              <div>
                <div className="text-sm font-medium text-primary">
                  Mobile push
                  <span className="ml-2 rounded-full border border-muted-foreground/30 px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                    Coming soon
                  </span>
                </div>
                <div className="text-xs text-muted-foreground">
                  Delivered through the Quanta mobile app once released.
                </div>
              </div>
            </div>
            <Switch
              checked={false}
              disabled
              data-testid="reminder-pref-push"
            />
          </div>
        </CardContent>
      </Card>

      <div className="text-xs text-muted-foreground">
        {(saving || reminderSaving) && (
          <span className="inline-flex items-center gap-1">
            <Loader2 className="h-3 w-3 animate-spin" /> Saving…
          </span>
        )}
      </div>
    </div>
  );
}
